/*
 * KC Theme — minimal interaction layer.
 * Handles: dialog drawers (search + cart), predictive search, AJAX add-to-cart.
 * No dependencies. Progressive enhancement: everything degrades to normal links/forms
 * if JS is disabled or fails to load.
 */
(() => {
  const root = (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';

  /* ---------- helpers ---------- */
  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const debounce = (fn, wait = 250) => {
    let t;
    return (...args) => {
      clearTimeout(t);
      t = setTimeout(() => fn(...args), wait);
    };
  };
  const openDialog = (id) => {
    const d = document.getElementById(id);
    if (d && typeof d.showModal === 'function' && !d.open) d.showModal();
  };
  const closeDialog = (id) => {
    const d = document.getElementById(id);
    if (d && d.open) d.close();
  };

  /* ---------- open drawers via any [data-drawer-open="ID"] ---------- */
  document.addEventListener('click', (e) => {
    const opener = e.target.closest('[data-drawer-open]');
    if (opener) {
      e.preventDefault();
      openDialog(opener.getAttribute('data-drawer-open'));
      return;
    }
    // Close button
    const closer = e.target.closest('[data-drawer-close]');
    if (closer) {
      e.preventDefault();
      const dlg = closer.closest('dialog');
      if (dlg) dlg.close();
      return;
    }
    // Quick view — fetch the product's quick-view section and open the dialog
    const qv = e.target.closest('[data-quick-view]');
    if (qv) {
      e.preventDefault();
      const content = document.getElementById('QuickView-content');
      if (!content) return;
      content.innerHTML = '<div class="p-16 text-center opacity-60">…</div>';
      openDialog('QuickView');
      fetch(`${qv.getAttribute('data-quick-view')}?section_id=quick-view`)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const inner = doc.querySelector('#quick-view-inner');
          content.innerHTML = inner ? inner.outerHTML : html;
        })
        .catch(() => {
          content.innerHTML = '<div class="p-16 text-center opacity-60">Unable to load.</div>';
        });
      return;
    }
    // Load more (collection) — fetch next page and append product cards
    const loadMore = e.target.closest('[data-load-more]');
    if (loadMore) {
      e.preventDefault();
      const url = loadMore.getAttribute('data-load-more');
      loadMore.setAttribute('aria-busy', 'true');
      fetch(url)
        .then((r) => r.text())
        .then((html) => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const grid = document.getElementById('ProductGrid');
          const newItems = doc.querySelectorAll('#ProductGrid > *');
          newItems.forEach((n) => grid.appendChild(n));
          const nextBtn = doc.querySelector('[data-load-more]');
          if (nextBtn) {
            loadMore.setAttribute('data-load-more', nextBtn.getAttribute('data-load-more'));
            loadMore.removeAttribute('aria-busy');
          } else {
            loadMore.remove();
          }
        })
        .catch(() => loadMore.removeAttribute('aria-busy'));
      return;
    }
    // Click on backdrop (the dialog element itself, outside the inner panel) closes it
    if (e.target.tagName === 'DIALOG' && e.target.hasAttribute('data-drawer')) {
      const rect = e.target.querySelector('[data-drawer-panel]')?.getBoundingClientRect();
      if (rect) {
        const inside =
          e.clientX >= rect.left && e.clientX <= rect.right &&
          e.clientY >= rect.top && e.clientY <= rect.bottom;
        if (!inside) e.target.close();
      }
    }
  });

  /* ---------- predictive search ---------- */
  const searchInput = $('#SearchDrawer-input');
  const searchResults = $('#SearchDrawer-results');
  const searchDefault = $('#SearchDrawer-default');
  if (searchInput && searchResults) {
    const showDefault = (show) => {
      if (searchDefault) searchDefault.classList.toggle('hidden', !show);
    };

    const run = debounce(async (term) => {
      if (!term) {
        searchResults.innerHTML = '';
        showDefault(true);
        return;
      }
      showDefault(false);
      searchResults.setAttribute('aria-busy', 'true');
      try {
        const url =
          `${root}search/suggest?q=${encodeURIComponent(term)}` +
          `&resources[type]=product&resources[limit]=8&section_id=predictive-search`;
        const res = await fetch(url);
        const text = await res.text();
        // section rendering returns full section markup; extract inner results container
        const doc = new DOMParser().parseFromString(text, 'text/html');
        const inner = doc.querySelector('#predictive-results-inner');
        searchResults.innerHTML = inner ? inner.innerHTML : text;
      } catch (err) {
        searchResults.innerHTML = '';
      } finally {
        searchResults.removeAttribute('aria-busy');
      }
    }, 250);

    searchInput.addEventListener('input', (e) => run(e.target.value.trim()));
    // Native <dialog>.showModal() auto-focuses the [autofocus] input — no JS needed.
  }

  /* ---------- AJAX add-to-cart ---------- */
  const refreshCart = (sectionsHtml) => {
    const drawer = document.getElementById('CartDrawer');
    if (!drawer) return;
    if (sectionsHtml && sectionsHtml['cart-drawer']) {
      const doc = new DOMParser().parseFromString(sectionsHtml['cart-drawer'], 'text/html');
      const fresh = doc.querySelector('#CartDrawer-inner');
      const current = document.getElementById('CartDrawer-inner');
      if (fresh && current) current.replaceWith(fresh);
    }
  };

  document.addEventListener('submit', async (e) => {
    const form = e.target;
    if (!(form instanceof HTMLFormElement)) return;
    const action = form.getAttribute('action') || '';
    if (!action.includes('/cart/add')) return;

    e.preventDefault();
    const btn = form.querySelector('[type="submit"]');
    if (btn) btn.setAttribute('aria-disabled', 'true');

    try {
      const formData = new FormData(form);
      formData.append('sections', 'cart-drawer');
      const res = await fetch(`${root}cart/add.js`, {
        method: 'POST',
        headers: { Accept: 'application/javascript' },
        body: formData,
      });
      const data = await res.json();
      if (res.ok) {
        refreshCart(data.sections);
        openDialog('CartDrawer');
      }
    } catch (err) {
      // fall back to normal navigation
      form.submit();
    } finally {
      if (btn) btn.removeAttribute('aria-disabled');
    }
  });

  /* ---------- cart line updates (qty + remove) inside the drawer ---------- */
  const cartChange = async (line, quantity) => {
    const res = await fetch(`${root}cart/change.js`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/javascript' },
      body: JSON.stringify({ line, quantity, sections: 'cart-drawer' }),
    });
    const data = await res.json();
    refreshCart(data.sections);
  };

  document.addEventListener('click', (e) => {
    const remove = e.target.closest('[data-cart-remove]');
    if (remove) {
      e.preventDefault();
      cartChange(Number(remove.getAttribute('data-cart-remove')), 0);
    }
    // Quantity steppers (product page + cart)
    const step = e.target.closest('[data-qty-change]');
    if (step) {
      const wrap = step.parentElement;
      const input = wrap.querySelector('input[type="number"]');
      if (input) {
        const min = Number(input.min) || 0;
        const next = Math.max(min, (Number(input.value) || 0) + Number(step.getAttribute('data-qty-change')));
        if (next !== Number(input.value)) {
          input.value = next;
          input.dispatchEvent(new Event('change', { bubbles: true }));
        }
      }
    }
  });

  document.addEventListener('change', (e) => {
    const qty = e.target.closest('[data-cart-qty]');
    if (qty) {
      cartChange(Number(qty.getAttribute('data-cart-qty')), Number(qty.value));
    }
    // Collection facets — auto-apply on change (native GET submit; works no-JS too)
    const facet = e.target.closest('[data-facet-form]');
    if (facet && (e.target.matches('input') || e.target.matches('select'))) {
      facet.submit();
    }
    // Cart page quantity — submit the update form on change (native POST)
    const cartForm = e.target.closest('[data-cart-form]');
    if (cartForm && e.target.matches('input[type="number"]')) {
      cartForm.submit();
    }
  });

  /* ---------- product variant selector (sync hidden id, price, availability) ---------- */
  const pForm = document.getElementById('ProductForm');
  const variantData = $('[data-product-variants]');
  if (pForm && variantData) {
    let variants = [];
    try { variants = JSON.parse(variantData.textContent); } catch (e) {}
    const idInput = pForm.querySelector('input[name="id"]');
    const priceEl = document.getElementById('ProductPrice');
    const submit = document.getElementById('ProductSubmit');

    // Unique option group names in DOM order (supports <select> AND radio pills)
    const groups = [];
    pForm.querySelectorAll('[name^="options"]').forEach((el) => {
      if (!groups.includes(el.name)) groups.push(el.name);
    });

    const chosenValues = () =>
      groups.map((name) => {
        const checked = pForm.querySelector(`input[name="${name}"]:checked`);
        if (checked) return checked.value;
        const sel = pForm.querySelector(`select[name="${name}"]`);
        return sel ? sel.value : '';
      });

    // Labels come from the markup so they stay translated.
    const labelEl = submit && submit.querySelector('[data-submit-label]');
    const label = (key, fallback) => (submit && submit.dataset[key]) || fallback;
    const setLabel = (text) => {
      if (labelEl) labelEl.textContent = text;
      else if (submit) submit.textContent = text;
    };

    const payment = pForm.querySelector('[data-payment-button]');

    const syncVariant = () => {
      const chosen = chosenValues();

      // Nothing (or not everything) picked yet — hold the add-to-cart back.
      if (chosen.some((v) => !v)) {
        if (idInput) idInput.value = '';
        if (payment) payment.hidden = true;
        if (submit) {
          submit.disabled = true;
          setLabel(label('chooseLabel', 'Choose options'));
        }
        return;
      }
      const match = variants.find((v) => v.options.every((o, i) => o === chosen[i]));
      if (!match) return;
      if (payment) payment.hidden = !match.available;
      if (idInput) idInput.value = match.id;
      if (priceEl) priceEl.textContent = match.price;
      if (submit) {
        submit.disabled = !match.available;
        setLabel(match.available ? label('addLabel', 'Add to cart') : label('soldLabel', 'Sold out'));
      }
    };
    pForm.addEventListener('change', (ev) => {
      if (ev.target.matches('[name^="options"]')) syncVariant();
    });
    // Reflect the initial state (e.g. nothing pre-selected → button disabled).
    syncVariant();
  }
})();
